import {
  SESSION_STATUS_LABELS,
  type Session,
  type SessionStatus,
  type SessionSummary,
} from '@diana/contracts';
import type { StatusTone } from '../../ui/index.js';

export const sessionStatuses = Object.keys(SESSION_STATUS_LABELS) as SessionStatus[];

export function giftWorkingRole(status: SessionStatus): 'buyer' | 'recipient' {
  return ['Scheduled', 'Editing', 'Delivered'].includes(status) ? 'recipient' : 'buyer';
}
export function giftPeopleOrder(
  status: SessionStatus,
  roles: NonNullable<Session['giftContacts']>,
) {
  const ids =
    giftWorkingRole(status) === 'recipient'
      ? [roles.recipientId, roles.buyerId]
      : [roles.buyerId, roles.recipientId];
  return [...new Set(ids.filter((id): id is string => Boolean(id)))];
}
export function primaryClient(session: Pick<SessionSummary, 'clients'> | Pick<Session, 'clients'>) {
  return session.clients.find((client) => client.isPrimary) ?? session.clients[0]!;
}

/** Gift contact changes never reassign purchase attribution. */
export function financialClient(
  session: Pick<SessionSummary, 'clients' | 'giftContacts' | 'isGiftCard'>,
) {
  return (
    (session.giftContacts
      ? session.clients.find((client) => client.id === session.giftContacts!.buyerId)
      : null) ?? primaryClient(session)
  );
}

export function primaryContact(client: SessionSummary['clients'][number]) {
  return client.email ?? client.phone ?? client.instagram ?? 'No contact details';
}

export function statusTone(status: SessionStatus): StatusTone {
  if (status === 'NewRequest') return 'warning';
  if (status === 'Delivered') return 'success';
  if (status === 'Canceled') return 'danger';
  return 'neutral';
}

export function formatShortDate(value: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
}

export function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export function sessionDraft(
  session: Session,
): Omit<import('@diana/contracts').UpdateSessionRequest, 'expectedRevision'> {
  return {
    transactions: session.transactions ?? [],
    ...(session.isGiftCard !== undefined ? { isGiftCard: session.isGiftCard } : {}),
    title: session.title,
    status: session.status,
    sessionType: session.sessionType,
    scheduledStartAt: session.scheduledStartAt,
    scheduledEndAt: session.scheduledEndAt,
    location: session.location,
    priceAmountMinor: session.priceAmountMinor,
    expensesAmountMinor: session.expensesAmountMinor,
    currency: session.currency,
    notes: session.notes,
    internalNotes: session.internalNotes ?? null,
    galleryUrl: session.galleryUrl ?? null,
    requestedPackageKey: session.requestedPackageKey,
    requestedPackageTitle: session.requestedPackageTitle,
    requestedPackagePriceLabel: session.requestedPackagePriceLabel,
    sourcePath: session.sourcePath,
    clients: session.clients.map((client) => ({
      clientId: client.id,
      position: client.position,
      isPrimary: client.isPrimary,
    })),
    checklist: session.checklist,
  };
}

export function localDateTime(value: string | null) {
  if (!value) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function isoDateTime(value: string) {
  return value ? new Date(value).toISOString() : null;
}

export function instagramUrl(value: string) {
  return `https://www.instagram.com/${value.trim().replace(/^@/, '')}/`;
}
